import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { UtilisateurService } from '../../../_services/utilisateur/utilisateur.service';
import { EvenementService } from '../../../_services/evenement/evenement.service';
import { RessourceService } from '../../../_services/ressource/ressource.service';
import { ActualiteService } from '../../../_services/actualite/actualite.service';
import { Evenement } from '../../../_interfaces/evenement';
import { Actualite } from '../../../_interfaces/actualite';
import { InscriptionService } from '../../../_services/inscription/inscription.service';
import { AdminPageHeaderComponent } from '../../../_components/admin-page-header/admin-page-header.component';
import { AuthService } from '../../../_services/auth/auth.service';
import { Observable } from 'rxjs';
import { Utilisateur } from '../../../_interfaces/utilisateur';

interface MonthBar {
  label: string;
  count: number;
  isCurrent: boolean;
}

@Component({
    selector: 'app-admin-home',
    imports: [CommonModule, RouterModule, AdminPageHeaderComponent],
    templateUrl: './admin-home.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './admin-home.component.scss'
})
export class AdminHomeComponent implements OnInit {
  private utilisateurService = inject(UtilisateurService);
  private evenementService = inject(EvenementService);
  private actualiteService = inject(ActualiteService);
  private ressourceService = inject(RessourceService);
  private inscriptionService = inject(InscriptionService);
  private authService = inject(AuthService);

  currentUser$: Observable<Utilisateur | null> = this.authService.currentUser$;

  nbMembresInscrits = 0;
  nbSortieAnnee = 0;
  nbActualites = 0;
  nbRessources = 0;
  nbEvenementsAVenir = 0;
  nbInscritsProchaineSortie = 0;

  prochaineSortie: Evenement | null = null;
  prochainesSorties: Evenement[] = [];
  recentesActualites: Actualite[] = [];

  monthlyDistribution: MonthBar[] = [];
  maxMonthly = 0;

  loading = true;

  ngOnInit() {
    this.utilisateurService.readAllUtilisateurs().subscribe((data) => {
      this.nbMembresInscrits = data.length;
    });

    this.evenementService.readAllEvenements().subscribe((data) => {
      const now = new Date();
      const currentYear = now.getFullYear();

      this.nbSortieAnnee = data.filter(e => new Date(e.dateDebut).getFullYear() === currentYear).length;
      this.nbEvenementsAVenir = data.filter(e => new Date(e.dateDebut) >= now).length;
      this.computeMonthlyDistribution(data, currentYear);
    });

    this.actualiteService.readAllActualites().subscribe((data) => {
      this.nbActualites = data.filter(a => (a.statut ?? 'publie') === 'publie').length;
      this.recentesActualites = data.slice(0, 3);
    });

    this.ressourceService.getAllRessources().subscribe({
      next: (data) => { this.nbRessources = data?.length ?? 0; },
      error: () => {}
    });

    this.evenementService.readXEvenements(4).subscribe((data) => {
      this.prochainesSorties = data;
      this.prochaineSortie = data[0] ?? null;
      if (this.prochaineSortie?.key) {
        this.inscriptionService.readAllInscriptionOfEvenement(this.prochaineSortie.key).then((inscriptions) => {
          this.nbInscritsProchaineSortie = inscriptions.filter(i => i.is_present === true).length;
        });
      }
      this.loading = false;
    });
  }

  private computeMonthlyDistribution(evenements: Evenement[], year: number) {
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const counts = new Array(12).fill(0);
    const currentMonth = new Date().getMonth();

    for (const e of evenements) {
      const d = new Date(e.dateDebut);
      if (d.getFullYear() === year) {
        counts[d.getMonth()]++;
      }
    }
    this.maxMonthly = Math.max(...counts, 1);
    this.monthlyDistribution = months.map((label, i) => ({
      label,
      count: counts[i],
      isCurrent: i === currentMonth
    }));
  }

  greeting(): string {
    const h = new Date().getHours();
    if (h < 6) return 'Bonne nuit';
    if (h < 12) return 'Bonjour';
    if (h < 18) return 'Bon après-midi';
    return 'Bonsoir';
  }

  greetingFor(user: Utilisateur | null): string {
    const base = this.greeting();
    return user?.firstName ? `${base} ${user.firstName} !` : `${base} !`;
  }

  barHeight(count: number): number {
    if (this.maxMonthly === 0) return 4;
    return Math.max(6, Math.round((count / this.maxMonthly) * 100));
  }

  formatDate(value: string | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  formatTime(value: string | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }
}
